import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { sendFamilyInvite } from '../../services/family.service';
import { SuccessModal } from '@beholos/holoscommon';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FamilyInviteFormProps = {
  onInviteSent?: () => void;
  disabled?: boolean;
  maxAllowed?: number;
  currentCount?: number;
};

export function FamilyInviteForm({
  onInviteSent,
  disabled = false,
  maxAllowed,
  currentCount = 0,
}: FamilyInviteFormProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isLimitReached = useMemo(() => {
    if (!maxAllowed) return false;
    return currentCount >= maxAllowed;
  }, [currentCount, maxAllowed]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!emailRegex.test(email.trim())) {
      setError('Por favor ingresa un correo válido.');
      return;
    }

    try {
      setLoading(true);
      const message = await sendFamilyInvite(email.trim());
      setSuccessMessage(message);
      setEmail('');
      onInviteSent?.();
    } catch (err: any) {
      setError(err?.message ?? 'No se pudo enviar la invitación.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex flex-col gap-2 mb-4">
        <h3 className="text-xl font-semibold text-gray-900">Invitar a un familiar</h3>
        <p className="text-sm text-gray-600">
          Regala acceso a Holos. Envía una invitación para que tu familiar empiece a disfrutar de muchas experiencias.
        </p>
        {maxAllowed && (
          <p className="text-xs text-gray-500">
            Cupos disponibles: {Math.max(maxAllowed - currentCount, 0)} de {maxAllowed}
          </p>
        )}
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-2 text-sm font-medium text-gray-800">
          Correo electrónico
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black/10"
            placeholder="correo@ejemplo.com"
            disabled={loading || disabled || isLimitReached}
            required
          />
        </label>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {successMessage && (
          <SuccessModal title={successMessage} message={`
            ${<strong>Plan Digital</strong>}: Acceso a recursos online sin costo.
            ${<strong>Plan de Créditos</strong>}: Precios preferenciales en experiencias presenciales y online.
            ${<strong>Importante</strong>}: Si ya tiene cuenta en Holos, solo debe iniciar sesión para activar el beneficio.
        `} onClose={() => setSuccessMessage(null)} isOpen={true} />
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={loading || disabled || isLimitReached}
            className="inline-flex items-center justify-center rounded-full bg-black px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-black/85 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {loading ? 'Enviando...' : 'Enviar invitación'}
          </button>
          {(disabled || isLimitReached) && (
            <span className="text-xs text-gray-600">
              Límite de invitaciones alcanzado.
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
